import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks/useAppStore';
import {
  getActiveStep,
  getCommissionMode, getMemberEmails,
  getSelectedCommissionPlan,
} from '../../../../store/commissions/selectors';
import { ROUTES } from '../../../../data';

type TCreateCommissionFooter = {
  handleBack: () => void,
  handleNext: () => void,
  stepsLength: number
  isNextDisabled: boolean
};

const CreateCommissionFooter: FC<TCreateCommissionFooter> = ({
  handleBack,
  handleNext,
  stepsLength,
  isNextDisabled,
}) => {
  const { t } = useTranslation(['common', 'commissions', 'tasklists']);
  const activeStep = useAppSelector(getActiveStep);
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const isLight = theme.palette.mode === 'light';
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const commissionMode = useAppSelector(getCommissionMode);
  const membersEmails = useAppSelector(getMemberEmails);
  const mode = useAppSelector(getCommissionMode);
  const navigate = useNavigate();

  const submitBtnText = commissionMode === 'create'
    ? `${t('createPlan', { ns: 'commissions' })} ${membersEmails.length > 0 ? `& ${t('assign', { ns: 'tasklists' })}` : ''}`
    : t('updatePlan', { ns: 'commissions' });

  const isFirstStepEmpty = useMemo(() => {
    if (selectedCommissionPlan?.type === 'Products' && activeStep === 2 && !selectedCommissionPlan.name?.trim()) {
      return true;
    }

    if (selectedCommissionPlan?.type === 'Products' && activeStep === 1 && selectedCommissionPlan.productCommissions?.length === 0) {
      return true;
    }

    if (selectedCommissionPlan?.type === 'Steps' && activeStep === 2 && selectedCommissionPlan.commissionSteps.length > 0) {
      const firstStep = selectedCommissionPlan.commissionSteps[0];
      return ((!firstStep.amount || firstStep.amount === '') && (!firstStep.percent || firstStep.percent === 0)) || !firstStep.to || firstStep.to === '';
    }
    return false;
  }, [selectedCommissionPlan]);

  const isBackToCommissions = useMemo(() => mode === 'edit' && activeStep === 1, [mode, activeStep]);

  const onBackClicked = () => {
    if (isBackToCommissions) {
      navigate(ROUTES.COMMISSION_PLANS);
    } else {
      handleBack();
    }
  };

  return (
    <AppBar position="fixed" color="primary" sx={{ top: 'auto', bottom: 0, background: isLight ? 'white' : '' }}>
      <Toolbar sx={{ justifyContent: 'flex-end', mr: 4 }}>
        {!isMobile && selectedCommissionPlan.type !== 'Products' && (
          <Typography
            sx={{
              mr: 6,
            }}
            color="text.secondary"
          >
            {`${selectedCommissionPlan.type === 'Fixed'
              ? t('fixedPlanSelected', { ns: 'commissions' })
              : t('stepsPlanSelected', { ns: 'commissions' })}`}
          </Typography>
        )}
        <Button
          color="primary"
          variant="outlined"
          disabled={activeStep === 0}
          onClick={onBackClicked}
          startIcon={<ArrowBackIcon />}
        >
          {isBackToCommissions ? t('backToCommissions', { ns: 'commissions' }) : t('back')}
        </Button>
        <Button
          sx={{
            ml: 2,
          }}
          {...(activeStep !== stepsLength - 1 && {
            endIcon: <ArrowForwardIcon />,
          })}
          color="primary"
          variant="contained"
          onClick={handleNext}
          disabled={isNextDisabled || isFirstStepEmpty}
        >
          {activeStep === stepsLength - 1 ? submitBtnText : t('next')}
        </Button>
      </Toolbar>
    </AppBar>
  );
};

export {
  CreateCommissionFooter,
};
